import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { DataGrid, type GridColDef, GridToolbar, type GridPaginationModel } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SportsCricketIcon from '@mui/icons-material/SportsCricket';
import type { Team, PaginatedResponse } from '../../models';
import { teamApi } from '../../services/api';
import { Link } from 'react-router-dom';
import TeamEditDialog from '../../components/Cricket/TeamEditDialog';

const Teams: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [editingTeam, setEditingTeam] = useState<Partial<Team>>({});
  const [isNew, setIsNew] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [totalCount, setTotalCount] = useState<number>(0);
  const [inputSearchTerm, setInputSearchTerm] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchTeams = useCallback(async (page: number, pageSize: number, search: string = '' ) => {
    setLoading(true);
    try {
      const response = await teamApi.getAll({ 
        page: page + 1, // API uses 1-based pagination
        page_size: pageSize,
        search: search
      });
      
      const typedResponse = response.data as PaginatedResponse<Team>;
      setTeams(typedResponse.results);
      setTotalCount(typedResponse.count);
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = useCallback((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, []);
  
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setInputSearchTerm(newSearchTerm);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(newSearchTerm);
    }, 500);
  }, []);

  useEffect(() => {
    fetchTeams(paginationModel.page, paginationModel.pageSize, searchTerm);
  }, [paginationModel, searchTerm]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleEdit = (team: Team) => {
    setEditingTeam(team);
    setIsNew(false);
    setOpen(true);
  };

  const handleAdd = () => {
    setEditingTeam({
      name: '',
      is_international: false,
      is_test_playing: false,
      is_odi_playing: false,
      is_active: true,
    });
    setIsNew(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };



  const handleSave = async () => {
    try {
      if (isNew) {
        await teamApi.create(editingTeam);
      } else {
        await teamApi.update(editingTeam.id!, editingTeam);
      }
      fetchTeams(paginationModel.page, paginationModel.pageSize, searchTerm);
      setOpen(false);
    } catch (error) {
      console.error('Error saving team:', error);
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'image', 
      headerName: 'Logo', 
      width: 80,
      sortable: false,
      renderCell: (params) => {
        // Show image with priority, fallback to flag if available
        const imageUrl = params.row.image || params.row.flag || null;
        return imageUrl ? (
          <img 
            src={imageUrl} 
            alt={params.row.name} 
            style={{ 
              width: '40px', 
              height: '40px', 
              objectFit: 'contain',
              borderRadius: params.row.image ? '4px' : '0px' // Round corners for logos
            }}
          />
        ) : (
          <Box sx={{ 
            width: '40px', 
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: params.row.color || 'grey.300',
            color: 'white',
            borderRadius: '4px'
          }}>
            {params.row.abbreviation?.charAt(0) || params.row.name.charAt(0)}
          </Box>
        );
      }
    },
    { field: 'name', headerName: 'Name', width: 100 },
    { field: 'long_name', headerName: 'Long Name', width: 200 },
    { field: 'abbreviation', headerName: 'Abr', width: 120, renderCell: (params) => `${params.row.abbreviation} ${params.row.e_o_id??''}`},
    { field: 'd_name', headerName: 'Dream', width: 120, renderCell: (params) => params.row.d_name? `${params.row.d_name} (${params.row.d_id})` : '-' },
    { field: 'c_name', headerName: 'Circle', width: 120, renderCell: (params) => params.row.c_name? `${params.row.c_name} (${params.row.c_id})` : '-' },
    { field: 'v_name', headerName: 'Vision', width: 120},
    // { field: 'f_name', headerName: 'Fab11', width: 120, renderCell: (params) => params.row.f_name? `${params.row.f_name} (${params.row.f_id})` : '-' },
    {
      field: 'is_international',
      headerName: 'Int',
      width: 50,
      renderCell: (params) => (params.value ? 'Yes' : 'No'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View">
            <IconButton color="primary" component={Link} to={`/teams/${params.row.id}/view`}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton color="info" onClick={() => handleEdit(params.row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Matches">
            <IconButton color="secondary" component={Link} to={`/matches?team=${params.row.id}`}>
              <SportsCricketIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: "calc(100vh - 100px)", width: "100%" }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Teams
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => fetchTeams(paginationModel.page, paginationModel.pageSize, searchTerm)}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Add Team
          </Button>
        </Box>
      </Box>
      
      {/* Search Box */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search teams by name..."
          value={inputSearchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <Typography variant="caption" color="text.secondary">
                  Searching...
                </Typography>
              </InputAdornment>
            ) : null,
          }}
          variant="outlined"
          size="small"
        />
      </Box>
      
      <Paper sx={{ height: 'calc(100% - 120px)', width: '100%' }}>
        <DataGrid
          rows={teams}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          pageSizeOptions={[25, 50, 100]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          rowCount={totalCount}
          disableColumnFilter
          density="compact"
        />
      </Paper>

      <TeamEditDialog
        open={open}
        onClose={handleClose}
        onSave={handleSave}
        team={editingTeam}
        onTeamChange={setEditingTeam}
        isNew={isNew}
        loading={loading}
      />
    </Box>
  );
};

export default Teams;
